import React from "react";
import toast, { Toaster } from "react-hot-toast";
import { Formik, Form, Field } from 'formik';
import { IKContext, IKUpload } from 'imagekitio-react';
import { createEvent, updateEvent } from "../../API/events";
import "./addEvents.css"; // Updated CSS file name
// import "./AddChallenge.css"
import { imageKitPublicKey, imageKitUrlEndpoint, uri } from "../../helper";

// const urlEndpoint = "https://ik.imagekit.io/vecch6x6p";
// const publicKey = "public_j5S33KH3AQ/NdlZ3jumN7sZ6AmY=";
// const authenticationEndpoint = `${uri}/challenge/auth`;

interface AddEventProps {
    handleClose: () => void;
    data?: {
        _id?: string;
        title?: string;
        startDate?: string;
        endDate?: string;
        days?: number;
        minDuration?: number;
        maxDuration?: number;
        minAge?: number;
        maxAge?: number;
        mode?: string;
        overview?: string;
        isActive?: boolean;
        isCompleted?: boolean;
        imageUrl?: string;
        outcome?: string[];
        demoUrl?: string;
    };
    changeIsActive?: boolean;
}

interface FormValuesType {
    title: string;
    startDate: string;
    endDate: string;
    days: number;
    minDuration: number;
    maxDuration: number;
    minAge: number;
    maxAge: number;
    mode: string;
    overview: string;
    imageUrl: string | null;
    outcome: string[];
    demoUrl: string;
}

const AddEvent: React.FC<AddEventProps> = (props) => {
    const onError = (error: any) => {
        
        toast.error("Failed to upload image");
    };

    const validateFields = (values: FormValuesType): boolean => {
        if (!values.title.trim()) {
            toast.error("Title is required");
            return false;
        }
        if (!values.startDate.trim()) {
            toast.error("Start Date is required");
            return false;
        }
        if (!values.endDate.trim()) {
            toast.error("End Date is required");
            return false;
        }
        if (isNaN(values.days) || values.days < 0) {
            toast.error("Valid Days is required");
            return false;
        }
        if (isNaN(values.minDuration) || values.minDuration < 0) {
            toast.error("Valid Min Duration is required");
            return false;
        }
        if (isNaN(values.maxDuration) || values.maxDuration < 0) {
            toast.error("Valid Max Duration is required");
            return false;
        }
        if (values.minDuration > values.maxDuration) {
            toast.error("Min Duration cannot exceed Max Duration");
            return false;
        }
        if (isNaN(values.minAge) || values.minAge < 0) {
            toast.error("Valid Min Age is required");
            return false;
        }
        if (isNaN(values.maxAge) || values.maxAge < 0) {
            toast.error("Valid Max Age is required");
            return false;
        }
        if (values.minAge > values.maxAge) {
            toast.error("Min Age cannot exceed Max Age");
            return false;
        }
        if (!values.mode.trim()) {
            toast.error("Mode is required");
            return false;
        }
        if (!values.overview.trim()) {
            toast.error("Overview is required");
            return false;
        }
        const validModes = ["Online", "Offline", "Hybrid"];
        if (!validModes.includes(values.mode)) {
            toast.error("Mode must be Online, Offline, or Hybrid");
            return false;
        }
        return true;
    };

    const handleSubmit = async (values: FormValuesType) => {
        if (!validateFields(values)) return;

        try {
            const eventData = {
                ...(props.data?._id && { _id: props.data._id }),
                title: values.title,
                startDate: values.startDate,
                endDate: values.endDate,
                days: values.days,
                minDuration: values.minDuration,
                maxDuration: values.maxDuration,
                minAge: values.minAge,
                maxAge: values.maxAge,
                mode: values.mode,
                overview: values.overview,
                isActive: props.changeIsActive ? !props.data?.isActive : (props.data?.isActive || false),
                isCompleted: props.data?.isCompleted || false,
                imageUrl: values.imageUrl || props.data?.imageUrl || "",
                outcome: values.outcome.length > 0 && values.outcome[0] !== "" ? values.outcome : props.data?.outcome || [],
                demoUrl: values.demoUrl || props.data?.demoUrl || "",
            };

            const eventString = JSON.stringify(eventData);

            if (props.data) {
                await updateEvent(eventString);
                toast.success(`Workshop successfully ${props.changeIsActive ? (props.data.isActive ? "deactivated" : "activated") : "updated"}`);
            } else {
                await createEvent(eventString);
                toast.success("Workshop successfully created");
            }
            setTimeout(() => props.handleClose(), 2000);
        } catch (e) {
            console.error("Error submitting workshop:", e);
            toast.error("Failed to save workshop. Please try again.");
        }
    };

    const headerTitle = props.changeIsActive && props.data ? "Activate/Deactivate Workshop"
        : props.data ? "Update Workshop" : "Add New Workshop";

    return (
        <>
            <Toaster toastOptions={{ style: { color: "black" } }} />
            <div className="addKids">
                <div className="fullDesc">
                    <div className="headerAK">
                        <h2>{headerTitle}</h2>
                        <div className="top-svg" onClick={props.handleClose}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="#8E8EA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </div>
                    <div className="ad-body">
                        <Formik
                            initialValues={{
                                title: props.data?.title || "",
                                startDate: props.data?.startDate || "",
                                endDate: props.data?.endDate || "",
                                days: props.data?.days || 0,
                                minDuration: props.data?.minDuration || 0,
                                maxDuration: props.data?.maxDuration || 0,
                                minAge: props.data?.minAge || 0,
                                maxAge: props.data?.maxAge || 0,
                                mode: props.data?.mode || "",
                                overview: props.data?.overview || "",
                                imageUrl: props.data?.imageUrl || null,
                                outcome: props.data?.outcome || [""],
                                demoUrl: props.data?.demoUrl || "",
                            }}
                            onSubmit={() => { }} // Handled via button
                        >
                            {({ values, setFieldValue }) => (
                                <Form>
                                    {/* Event Details Section */}
                                    <div className="form-section">
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="title">Workshop Title*</label>
                                            <Field id="title" name="title" className="form-control" placeholder="Enter workshop title" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="startDate">Start Date*</label>
                                            <Field type="date" id="startDate" name="startDate" className="form-control" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="endDate">End Date*</label>
                                            <Field type="date" id="endDate" name="endDate" className="form-control" />
                                        </div>
                                    </div>

                                    {/* Duration and Age Section */}
                                    <div className="form-section">
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="days">Days*</label>
                                            <Field type="number" id="days" name="days" className="form-control" min="0" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Duration Limit (minutes)*</label>
                                            <div style={{ display: 'flex', gap: '16px' }}>
                                                <Field type="number" id="minDuration" name="minDuration" className="form-control" placeholder="Min" min="0" />
                                                <Field type="number" id="maxDuration" name="maxDuration" className="form-control" placeholder="Max" min="0" />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Age Limit*</label>
                                            <div style={{ display: 'flex', gap: '16px' }}>
                                                <Field type="number" id="minAge" name="minAge" className="form-control" placeholder="Min" min="0" />
                                                <Field type="number" id="maxAge" name="maxAge" className="form-control" placeholder="Max" min="0" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Details Section */}
                                    <div className="form-section">
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="mode">Mode*</label>
                                            <Field as="select" id="mode" name="mode" className="form-control">
                                                <option value="">Select Mode</option>
                                                <option value="Online">Online</option>
                                                <option value="Offline">Offline</option>
                                                <option value="Hybrid">Hybrid</option>
                                            </Field>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="overview">Overview*</label>
                                            <Field as="textarea" id="overview" name="overview" className="form-control" style={{ height: '100px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="demoUrl">Demo URL</label>
                                            <Field id="demoUrl" name="demoUrl" className="form-control" placeholder="Enter demo URL" />
                                        </div>
                                    </div>

                                    {/* Outcomes and Image Upload */}
                                    <div className="form-section">
                                        <div className="form-group">
                                            <label className="form-label">Outcomes (comma-separated)</label>
                                            <Field
                                                type="text"
                                                name="outcome"
                                                className="form-control"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    setFieldValue("outcome", e.target.value.split(",").map(item => item.trim()))
                                                }
                                                value={values.outcome.join(", ")}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Upload Image</label>
                                            <IKContext publicKey={imageKitPublicKey}
                                                urlEndpoint={imageKitUrlEndpoint}
                                                authenticationEndpoint={`${uri}/challenge/auth`}>
                                                <IKUpload
                                                    fileName={values.title || "event-image"}
                                                    onError={onError}
                                                    onSuccess={(res: any) => setFieldValue("imageUrl", res.url)}
                                                    className="form-control"
                                                    style={{ cursor: 'pointer', textAlign: 'center' }}
                                                />
                                                {values.imageUrl && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <img src={values.imageUrl} alt="Preview" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                                                    </div>
                                                )}
                                            </IKContext>
                                        </div>
                                    </div>

                                    <div className="drag-drop-top-line"></div>
                                    <div className="down-buttons d-flex">
                                        <button
                                            type="button"
                                            className="create-button"
                                            onClick={() => handleSubmit(values)}
                                        >
                                            {props.changeIsActive ? (props.data?.isActive ? "Deactivate Workshop" : "Activate Workshop") : (props.data ? "Update Event" : "Add Event")}
                                        </button>
                                        <button type="button" className="cancel-button" onClick={props.handleClose}>Cancel</button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    );
};

export { AddEvent };